// FF-1129 (Analytics Outbound v1 — app GA4 light, ZEE port)
//
// Injectable consent gate for the app (ff-dashboard-zee).
// Mirrors the lander's attribution-consent isTrackingSuppressed pattern so the
// same opt-out cookie (ff_tracking_optout) and GPC signal suppress GA4 in the
// app as they do on public pages.
//
// All browser access goes through AppConsentEnv so this module is unit-testable
// without a DOM.

/** Cookie name shared with the lander consent gate (FF-1127). */
export const OPT_OUT_COOKIE = 'ff_tracking_optout';

/** Minimal environment contract — injectable for tests. */
export interface AppConsentEnv {
  /** Returns true when navigator.globalPrivacyControl is set. */
  isGpc: () => boolean;
  /** Returns the opt-out cookie value, or null if absent. */
  getOptOutCookie: () => string | null;
}

/**
 * Default browser-backed environment. Guards against SSR by degrading to
 * safe defaults (no GPC, no opt-out) when browser globals are absent.
 */
export const browserConsentEnv: AppConsentEnv = {
  isGpc() {
    return (
      typeof navigator !== 'undefined' &&
      (navigator as Navigator & { globalPrivacyControl?: boolean })
        .globalPrivacyControl === true
    );
  },
  getOptOutCookie() {
    if (typeof document === 'undefined') return null;
    const match = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${OPT_OUT_COOKIE}=`));
    return match
      ? decodeURIComponent(match.slice(OPT_OUT_COOKIE.length + 1))
      : null;
  },
};

/**
 * Returns true when tracking should be suppressed — either because the visitor
 * has GPC enabled or because they explicitly opted out via the consent banner.
 * When suppressed, no GA4 init or event send should occur.
 */
export function isAppTrackingSuppressed(
  env: AppConsentEnv = browserConsentEnv
): boolean {
  return env.isGpc() || env.getOptOutCookie() === '1';
}
